import React from "react";
import { parseUtcLoose } from "../../../shared/utils/gatewayTime";
import {
  MachineConnectionDto,
  MachineStatusDto,
  SignalRConnState,
  SiteAgentDto,
} from "../models/edgeMonitorModels";
import MachinePicker from "./MachinePicker";

const FONT_SORA = "'Sora', system-ui, -apple-system, sans-serif";
const FONT_MONO = "'JetBrains Mono', 'SF Mono', Menlo, Consolas, monospace";

const COLORS = {
  green: "#2D8C4E",
  greenBg: "#E8EFE6",
  terracotta: "#D4572B",
  terracottaBg: "#FCEBE3",
  amber: "#D4B040",
  amberBg: "#F8F0DD",
  greyDot: "#8C9099",
  primaryText: "#1A1A1A",
  dimText: "#6B6B6B",
  border: "#E5E2D9",
  white: "#FFFFFF",
  pillLiveBg: "#1F2A1F",
  pillLiveText: "#FAFAF7",
};

export interface Zone1HeaderProps {
  machineId: string | null;
  machineName: string | null;
  machineNotFoundOrNoAccess: boolean;
  siteAgent: SiteAgentDto | null;
  /** True if `getSiteAgents` returned a 403 (Admin-gated). */
  siteAgentNoAccess: boolean;
  connection: MachineConnectionDto | null;
  status: MachineStatusDto | null;
  connState: SignalRConnState;
  /** ISO timestamp of most recent push (for "push Ns ago" + dot freshness). */
  lastPushAt: string | null;
  /** Polling rate in events per minute, last 60s window. */
  pollingRatePerMin: number;
  /** Total parse / push / S7 errors in last 5 minutes. */
  errorsLast5Min: number;
  /** ISO timestamp of when the 5m error window started (sublabel: "since {time}Z"). */
  errorWindowStartAt?: string | null;
  /** Total events received in the polling-rate window (sublabel: "{n}k events"). */
  pollingEventsInWindow?: number | null;
}

// `parseUtcLoose` MOVED, BODY UNCHANGED, to shared/utils/gatewayTime.ts — the
// gateway timestamp rule it encodes is a property of the WIRE, and the Machinery
// v2 profile now reads the same API from the customer side. One parser for one
// API; the docblock that used to sit here moved with it.

const relativeAgo = (iso: string | null | undefined, fallback = "—"): string => {
  const t = parseUtcLoose(iso);
  if (t === null) return fallback;
  const ms = Date.now() - t;
  if (ms < 0) return "just now";
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m${s % 60 ? `${s % 60}s` : ""} ago`;
  const h = Math.floor(m / 60);
  return `${h}h${m % 60 ? `${m % 60}m` : ""} ago`;
};

const formatTime = (iso: string | null | undefined): string => {
  const t = parseUtcLoose(iso);
  if (t === null) return "—";
  try {
    return new Date(t).toISOString().slice(11, 19) + "Z";
  } catch {
    return "—";
  }
};

const Pill: React.FC<{ state: SignalRConnState }> = ({ state }) => {
  let bg = COLORS.pillLiveBg;
  let dot = COLORS.green;
  let text = "Live";
  if (state === "reconnecting") {
    bg = COLORS.amberBg;
    dot = COLORS.amber;
    text = "Reconnecting";
  } else if (state === "polling") {
    bg = COLORS.amberBg;
    dot = COLORS.amber;
    text = "Polling";
  } else if (state === "offline") {
    bg = COLORS.terracottaBg;
    dot = COLORS.terracotta;
    text = "Offline";
  }
  const isLive = state === "live";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "4px 10px",
        borderRadius: "12px",
        background: bg,
        color: isLive ? COLORS.pillLiveText : COLORS.primaryText,
        fontFamily: FONT_MONO,
        fontSize: "11px",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
      }}
    >
      <span
        style={{
          width: "6px",
          height: "6px",
          borderRadius: "50%",
          background: dot,
        }}
      />
      {text}
    </span>
  );
};

const Field: React.FC<{
  label: string;
  children: React.ReactNode;
  alignRight?: boolean;
  minWidth?: number;
}> = ({ label, children, minWidth }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      gap: "4px",
      minWidth: minWidth ? `${minWidth}px` : undefined,
    }}
  >
    <div
      style={{
        fontFamily: FONT_MONO,
        fontSize: "10px",
        letterSpacing: "0.1em",
        color: COLORS.dimText,
        textTransform: "uppercase",
      }}
    >
      {label}
    </div>
    {children}
  </div>
);

const Zone1Header: React.FC<Zone1HeaderProps> = ({
  machineId,
  machineName,
  machineNotFoundOrNoAccess,
  siteAgent,
  siteAgentNoAccess,
  connection,
  status,
  connState,
  lastPushAt,
  pollingRatePerMin,
  errorsLast5Min,
  errorWindowStartAt,
  pollingEventsInWindow,
}) => {
  // E-stop banner
  const eStopBad =
    status?.signals && (status.signals as Record<string, unknown>).eStopOk === false;

  if (!machineId) {
    // Empty state — full hero block with picker as focal element. Matches
    // Paper artboard 4SG-0. Top breadcrumb strip lives above the hero;
    // Zones 2/3/4 placeholders are rendered by EdgeLiveMonitorPage at 0.55
    // opacity below this header.
    return (
      <div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "10px 32px",
            fontFamily: FONT_MONO,
            fontSize: "11px",
            color: COLORS.dimText,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            background: COLORS.white,
            borderBottom: `1px solid ${COLORS.border}`,
          }}
        >
          <div>
            / INTERNAL / TOOLS / EDGE-MONITOR
            <span style={{ margin: "0 12px", color: COLORS.dimText }}>·</span>
            STAFF DEBUG · v0.1
          </div>
          <span
            style={{
              alignItems: "center",
              backgroundColor: "#F2EDE2",
              borderRadius: "12px",
              display: "inline-flex",
              gap: "8px",
              padding: "5px 11px",
            }}
          >
            <span
              style={{
                background: COLORS.greyDot,
                width: "6px",
                height: "6px",
                borderRadius: "50%",
              }}
            />
            <span
              style={{
                color: "#6B7280",
                fontFamily: FONT_MONO,
                fontSize: "11px",
                fontWeight: 500,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
              }}
            >
              IDLE
            </span>
          </span>
        </div>

        <div
          style={{
            background: COLORS.white,
            borderBottom: `1px solid ${COLORS.border}`,
            padding: "48px 32px 56px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginBottom: "28px" }}>
            <div
              style={{
                fontFamily: FONT_MONO,
                fontSize: "10px",
                color: COLORS.dimText,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                fontWeight: 500,
              }}
            >
              No machine loaded · select to begin
            </div>
            <div
              style={{
                fontFamily: FONT_SORA,
                fontSize: "26px",
                fontWeight: 600,
                color: COLORS.primaryText,
                letterSpacing: "-0.015em",
                lineHeight: "32px",
              }}
            >
              Edge Live Monitor
            </div>
            <div
              style={{
                fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
                fontSize: "13px",
                color: "#6B7280",
                lineHeight: "20px",
                marginTop: "4px",
                maxWidth: "560px",
              }}
            >
              Choose a connected machine to inspect its agent, PLC signal grid, event stream, and edge diagnostics in real time.
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "10px", maxWidth: "680px" }}>
            <div
              style={{
                fontFamily: FONT_MONO,
                fontSize: "10px",
                color: COLORS.dimText,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                fontWeight: 500,
              }}
            >
              Machine
            </div>
            <MachinePicker
              currentMachineId={null}
              currentMachineName={null}
              variant="hero"
            />
            <div
              style={{
                fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
                fontSize: "12px",
                color: COLORS.dimText,
                lineHeight: "16px",
              }}
            >
              Or append <code style={{ background: "#F0EFE9", padding: "1px 5px", borderRadius: "3px", fontFamily: FONT_MONO }}>?machineId=&lt;guid&gt;</code> to the URL.
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (machineNotFoundOrNoAccess && !status) {
    return (
      <div style={emptyHintStyle}>
        <div style={{ fontFamily: FONT_SORA, fontSize: "18px", fontWeight: 600, color: COLORS.primaryText, marginBottom: "8px" }}>
          Machine not found or no access
        </div>
        <div style={{ fontFamily: FONT_MONO, fontSize: "12px", color: COLORS.dimText, wordBreak: "break-all" }}>
          machineId: {machineId}
        </div>
      </div>
    );
  }

  // Top breadcrumb strip + LIVE pill.
  // NOTE: `background: COLORS.white` is required — the parent wraps this
  // header in `position: sticky; top: 0; zIndex: 5` when a machine is loaded,
  // and Zone 2's signal grid scrolls underneath it. Without an opaque fill
  // the scrolled content bleeds through (visible bug 2026-05-07).
  const breadcrumb = (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "10px 32px",
        fontFamily: FONT_MONO,
        fontSize: "11px",
        color: COLORS.dimText,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        background: COLORS.white,
      }}
    >
      <div>
        / INTERNAL / TOOLS / EDGE-MONITOR
        <span style={{ margin: "0 12px", color: COLORS.dimText }}>·</span>
        STAFF DEBUG
        <span style={{ margin: "0 12px", color: COLORS.dimText }}>·</span>
        v0.1
      </div>
      <Pill state={connState} />
    </div>
  );

  const heartbeatMs = parseUtcLoose(siteAgent?.lastHeartbeat);
  const heartbeatOnline =
    heartbeatMs !== null && Date.now() - heartbeatMs < 60_000;
  const linkMs = parseUtcLoose(connection?.lastDataReceived);
  const linkUp = linkMs !== null && Date.now() - linkMs < 60_000;

  return (
    <div>
      {breadcrumb}

      {/* main strip */}
      <div
        style={{
          padding: "16px 32px 20px",
          display: "flex",
          alignItems: "stretch",
          gap: "32px",
          background: COLORS.white,
          borderBottom: `1px solid ${COLORS.border}`,
        }}
      >
        {/* machine picker — replaces former name+GUID block. Closed state by
            default; click opens dropdown anchored to this position. */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: FONT_MONO,
              fontSize: "10px",
              color: COLORS.dimText,
              textTransform: "uppercase",
              letterSpacing: "0.14em",
              marginBottom: "6px",
              fontWeight: 500,
            }}
          >
            {machineId.slice(0, 8).toUpperCase()} · MACHINE
          </div>
          <MachinePicker
            currentMachineId={machineId}
            currentMachineName={machineName}
            variant="compact"
          />
          <div
            style={{
              fontFamily: FONT_MONO,
              fontSize: "11px",
              color: COLORS.dimText,
              wordBreak: "break-all",
              marginTop: "8px",
              letterSpacing: "0.04em",
            }}
          >
            {machineId}
          </div>
        </div>

        {/* Polling rate — moved up from former Zone 4 footer */}
        <Field label="Polling Rate" minWidth={200}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontFamily: FONT_SORA, fontSize: "16px", fontWeight: 600 }}>
              {pollingRatePerMin} <span style={{ fontFamily: FONT_MONO, fontSize: "11px", color: COLORS.dimText, fontWeight: 400 }}>evt/min</span>
            </span>
          </div>
          <div style={{ fontFamily: FONT_MONO, fontSize: "11px", color: COLORS.dimText }}>
            last 60s
            {pollingEventsInWindow != null
              ? ` · ${
                  pollingEventsInWindow >= 1000
                    ? `${(pollingEventsInWindow / 1000).toFixed(2)}k`
                    : pollingEventsInWindow
                } events`
              : ""}
          </div>
          <div style={{ fontFamily: FONT_MONO, fontSize: "11px", color: COLORS.dimText }}>
            ingest healthy
          </div>
        </Field>

        {/* Errors last 5m — moved up from former Zone 4 footer */}
        <Field label="Errors Last 5m" minWidth={220}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span
              style={{
                fontFamily: FONT_SORA,
                fontSize: "16px",
                fontWeight: 600,
                color:
                  errorsLast5Min === 0
                    ? COLORS.primaryText
                    : errorsLast5Min > 5
                    ? COLORS.terracotta
                    : COLORS.amber,
              }}
            >
              {errorsLast5Min}
            </span>
          </div>
          <div style={{ fontFamily: FONT_MONO, fontSize: "11px", color: COLORS.dimText }}>
            since {formatTime(errorWindowStartAt)}
          </div>
          <div style={{ fontFamily: FONT_MONO, fontSize: "11px", color: COLORS.dimText }}>
            no parse / push / s7 errors
          </div>
        </Field>

        {/* Edge Agent (renamed from Site Agent — wired connection unchanged) */}
        <Field label="Edge Agent" minWidth={210}>
          {siteAgentNoAccess ? (
            <div style={{ fontFamily: FONT_MONO, fontSize: "12px", color: COLORS.dimText }}>
              no access
            </div>
          ) : (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    background: heartbeatOnline ? COLORS.green : COLORS.terracotta,
                  }}
                />
                <span style={{ fontFamily: FONT_SORA, fontSize: "16px", fontWeight: 600 }}>
                  {heartbeatOnline ? "Online" : "Offline"}
                </span>
              </div>
              <div style={{ fontFamily: FONT_MONO, fontSize: "11px", color: COLORS.dimText }}>
                heartbeat {relativeAgo(siteAgent?.lastHeartbeat)} · {formatTime(siteAgent?.lastHeartbeat)}
              </div>
              <div style={{ fontFamily: FONT_MONO, fontSize: "11px", color: COLORS.dimText }}>
                agent v{siteAgent?.version ?? "—"}
              </div>
            </>
          )}
        </Field>

        {/* S7 link / MachineConnection */}
        <Field label="S7 Link · CPU 315-2DP" minWidth={210}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                background: linkUp ? COLORS.green : COLORS.greyDot,
              }}
            />
            <span style={{ fontFamily: FONT_SORA, fontSize: "16px", fontWeight: 600 }}>
              {linkUp ? "Up" : "—"}
            </span>
          </div>
          <div style={{ fontFamily: FONT_MONO, fontSize: "11px", color: COLORS.dimText }}>
            last data {relativeAgo(connection?.lastDataReceived)}
          </div>
          <div
            style={{
              fontFamily: FONT_MONO,
              fontSize: "11px",
              color: COLORS.dimText,
              wordBreak: "break-all",
            }}
          >
            {connection?.connectionString ?? "—"}
          </div>
        </Field>

        {/* SignalR */}
        <Field label="SignalR" minWidth={180}>
          <Pill state={connState} />
          <div style={{ fontFamily: FONT_MONO, fontSize: "11px", color: COLORS.dimText, marginTop: "2px" }}>
            push {relativeAgo(lastPushAt)}
          </div>
          <div style={{ fontFamily: FONT_MONO, fontSize: "11px", color: COLORS.dimText, wordBreak: "break-all" }}>
            /hubs/gateway · machine-{machineId.slice(0, 4)}
          </div>
        </Field>
      </div>

      {/* E-stop banner */}
      {eStopBad && (
        <div
          style={{
            padding: "10px 32px",
            background: COLORS.terracottaBg,
            borderBottom: `1px solid ${COLORS.terracotta}`,
            color: COLORS.terracotta,
            fontFamily: FONT_MONO,
            fontSize: "13px",
            fontWeight: 600,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
          }}
        >
          ● E-STOP active — operator pressed E-stop button
        </div>
      )}
    </div>
  );
};

const emptyHintStyle: React.CSSProperties = {
  padding: "60px 40px",
  textAlign: "center",
  background: "#FFFFFF",
  border: "1px dashed #C9C5BB",
  borderRadius: "6px",
  margin: "32px 32px 0",
};

export default Zone1Header;
